import { Button } from '@/components/Button';
import { Tag } from '@/components/Tag';
import { LuExternalLink, LuSettings } from 'react-icons/lu';
import type {
  AgentApiCandidateFormsShow,
  AgentApiCandidateFormAnswersRecord,
} from '@/types/serializers';
import { useNavigate } from '@tanstack/react-router';
import { Route } from '@/routes/agent_spa/agent/form/index';
import useFetchCandidateFormAnswers from '@/features/agent/form/api/useFetchCandidateFormAnswers';

interface Props {
  candidateForm: AgentApiCandidateFormsShow;
}

function formatAnswerDate(createdAt: string): string {
  const date = new Date(createdAt);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}`;
}

function getUnhandledCount(records: AgentApiCandidateFormAnswersRecord[]): number {
  return records.filter((r) => r.status === 'created').length;
}

export default function FormHomeTab({ candidateForm }: Props) {
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: answersData, isLoading, error } = useFetchCandidateFormAnswers();
  const records = answersData?.records ?? [];
  const unhandledCount = getUnhandledCount(records);

  const handleSettingsClick = () => {
    navigate({
      to: '/agent/form/settings',
    });
  };

  const handleQuestionPreviewClick = () => {
    const url = candidateForm.question_page_url;
    if (url) window.open(url, '_blank');
  };

  const handleThanksPreviewClick = () => {
    const url = candidateForm.thanks_page_url;
    if (url) window.open(url, '_blank');
  };

  const handleAnswerClick = (answerId: number) => {
    navigate({
      to: '/agent/form',
      search: { tab: 'review', answerId },
      replace: false,
    });
  };

  return (
    <div className="mt-xl flex flex-col gap-sm">
      {/* 作成したエントリーフォーム */}
      <div className="flex items-center justify-between">
        <h2 className="text-heading-sm text-main">作成したエントリーフォーム</h2>
        <Button color="secondary" size="md" onClick={handleSettingsClick}>
          <LuSettings className="size-5" />
          基本設定
        </Button>
      </div>

      <div className="flex overflow-hidden rounded-sm shadow-[0px_0px_3px_0px_rgba(0,0,0,0.2)]">
        {/* 左側: ロゴ */}
        <div className="flex w-[515px] shrink-0 items-center justify-center border-r border-solid border-border bg-white p-lg">
          {candidateForm.logo_url ? (
            <img
              src={candidateForm.logo_url}
              alt={candidateForm.title}
              className="h-[120px] w-[250px] object-contain"
            />
          ) : (
            <div className="flex h-[120px] w-[250px] items-center justify-center">
              <span className="text-body-sm text-sub">ロゴなし</span>
            </div>
          )}
        </div>

        {/* 右側: フォーム情報 */}
        <div className="flex flex-1 flex-col items-start gap-md bg-white p-lg">
          <Tag color={candidateForm.status === 'published' ? 'green' : 'gray'} size="md">
            {candidateForm.status === 'published' ? '公開中' : '下書き'}
          </Tag>

          <h1 className="text-heading-lg text-main">{candidateForm.title}</h1>

          <div className="flex gap-sm">
            <Button color="secondary" size="md" onClick={handleQuestionPreviewClick}>
              質問ページプレビュー
              <LuExternalLink className="size-4" />
            </Button>
            <Button color="secondary" size="md" onClick={handleThanksPreviewClick}>
              サンクスページプレビュー
              <LuExternalLink className="size-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* 回答確認 */}
      <h2 className="text-heading-sm text-main">回答確認</h2>
      <div className="rounded-sm bg-white px-lg py-xs shadow-layer2">
        {error && (
          <p className="py-md text-body-sm text-sub">
            回答の読み込みに失敗しました。ページをリロードしてください。
          </p>
        )}
        {!error && isLoading && <p className="py-md text-body-sm text-sub">読み込み中...</p>}
        {!error && !isLoading && (
          <div className="flex flex-col gap-xs">
            <div className="flex flex-col gap-xs">
              <p className="text-body-sm text-sub">未対応の回答が{unhandledCount}件あります。</p>
              <div className="h-px w-full bg-border" />
            </div>
            {records.length === 0 ? (
              <p className="py-md text-body-sm text-sub">回答がありません</p>
            ) : (
              records.map((row, index) => {
                const name = row.candidate_profile_name;
                const suffix = name
                  ? 'さんがフォームに回答しました。'
                  : 'がフォームに回答しました。';
                return (
                  <div key={row.id} className="flex flex-col gap-xs">
                    <div className="flex items-center gap-lg text-body-sm text-sub">
                      <span>{formatAnswerDate(row.created_at)}</span>
                      <span>
                        <button
                          type="button"
                          onClick={() => handleAnswerClick(row.id)}
                          className="cursor-pointer text-primary hover:opacity-80"
                        >
                          {name ?? '匿名'}
                        </button>
                        {suffix}
                      </span>
                    </div>
                    {index < records.length - 1 && <div className="h-px w-full bg-border" />}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
